/**
 * Page-level, unscoped by department — a hired role not matched to Personio or an undated open
 * role is a data problem regardless of which tab you're looking at, so it is shared across every
 * tab rather than duplicated or filtered per department.
 *
 * It sits at the BOTTOM of `InternalPlaygroundHiringTimelineView` (David, Sep 2026). Almost none of
 * what it names contributes FTE to the table, so it is a footnote about the figures rather than
 * something to read before them — above the tab bar it pushed the first rows off the fold on every
 * load, for a notice that changes only when Airtable does. The exception is `leaveUnavailable`: then
 * the FTE is too HIGH (people on long-term leave are still counted), so it gets its own sentence
 * rather than sitting under "none of these contribute FTE".
 *
 * The row is laid out by this file, not by the atom: the design-system `Button` wraps its children
 * in its own centring flex div, so a `gap` on the button itself never reaches the icon and the
 * text (which is exactly how they came to sit flush against each other). `contentClassName` is the
 * prop that lands on that inner div.
 */
import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import type { HiringCapacityTimeline } from '../../services/internalPlaygroundApi';

/** Collapsed footnote listing what the figures leave out (and, for long-term leave, what they still count). */
export function DataQualityBanner({ dataQuality }: { dataQuality: HiringCapacityTimeline['dataQuality'] }) {
  const [open, setOpen] = useState(false);
  const notCounted =
    dataQuality.unmatchedHires.length +
    dataQuality.undatedDemandRoles.length +
    dataQuality.orgOverrideWarnings.length +
    (dataQuality.proposalsUnavailable ? 1 : 0) +
    (dataQuality.backfillsUnavailable ? 1 : 0);
  if (notCounted === 0 && !dataQuality.leaveUnavailable) return null;

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="!h-auto !min-h-0 w-full !rounded-md !px-0 !py-0 text-left text-amber-800 hover:!bg-transparent hover:text-amber-900"
        contentClassName="!justify-start gap-2.5"
      >
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span className="font-medium">
          {notCounted > 0 && (
            <>
              {dataQuality.unmatchedHires.length} hired role(s) not matched to Personio
              {dataQuality.undatedDemandRoles.length > 0 &&
                `, ${dataQuality.undatedDemandRoles.length} open role(s) with no date`}
              {dataQuality.orgOverrideWarnings.length > 0 &&
                `, ${dataQuality.orgOverrideWarnings.length} org-chart override warning(s)`}
              {dataQuality.proposalsUnavailable && `, proposed roles unavailable`}
              {dataQuality.backfillsUnavailable && `, backfill status unavailable`}
              {' — none of these contribute FTE to the table above.'}
            </>
          )}
          {dataQuality.leaveUnavailable &&
            `${notCounted > 0 ? ' ' : ''}Long-term leave unavailable: people on leave are still counted in FTE.`}
        </span>
        {open ? (
          <ChevronDown className="ml-auto h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="ml-auto h-4 w-4 shrink-0" />
        )}
      </Button>
      {open && (
        <div className="mt-3 space-y-3 border-t border-amber-200 pt-3 text-xs text-amber-900">
          {dataQuality.proposalsUnavailable && (
            <div>
              <p className="mb-1 font-medium">Proposed roles:</p>
              <p>{dataQuality.proposalsUnavailable}</p>
            </div>
          )}
          {dataQuality.backfillsUnavailable && (
            <div>
              <p className="mb-1 font-medium">Backfills:</p>
              <p>{dataQuality.backfillsUnavailable}</p>
            </div>
          )}
          {dataQuality.leaveUnavailable && (
            <div>
              <p className="mb-1 font-medium">Long-term leave:</p>
              <p>{dataQuality.leaveUnavailable}</p>
            </div>
          )}
          {dataQuality.orgOverrideWarnings.length > 0 && (
            <div>
              <p className="mb-1 font-medium">Org-chart override didn&apos;t match a real employee:</p>
              <ul className="space-y-0.5">
                {dataQuality.orgOverrideWarnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}
          {dataQuality.unmatchedHires.length > 0 && (
            <div>
              <p className="mb-1 font-medium">Hired, no linked Personio record:</p>
              <ul className="space-y-0.5">
                {dataQuality.unmatchedHires.map((h) => (
                  <li key={h.id}>
                    {h.positionId ? `#${h.positionId} — ` : ''}
                    {h.title} ({h.department}){h.actualStartDate ? ` — started ${h.actualStartDate}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {dataQuality.undatedDemandRoles.length > 0 && (
            <div>
              <p className="mb-1 font-medium">Open/Backlog/On Hold/proposed role with no start date at all:</p>
              <ul className="space-y-0.5">
                {dataQuality.undatedDemandRoles.map((r) => (
                  <li key={r.id}>
                    {r.positionId ? `#${r.positionId} — ` : ''}
                    {r.title} ({r.department}, {r.status})
                    {r.proposed && ' — proposed'}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
